import NotificationValidator from "Validators/Admin/Notification";
import { EditorState } from "draft-js";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router";

import AdminRoutes from "Routes/Components/Admin";
import AuthRoutes from "Routes/Components/Auth";

import { setAllFormValues } from "Helpers/Form";
import { addHTMLToEditor, transformEditorStateIntoData } from "Helpers/Util";

import { useFetch } from "Hooks/useFetch";
import { useFormSchema } from "Hooks/useFormSchema";
import useSegmentation from "Hooks/useSegmentation";

import Button from "Components/Admin/Buttons";
import DateValidity from "Components/Admin/DateValidity";
import FormWrapper from "Components/Admin/Form/FormWrapper";
import Input from "Components/Admin/Form/Input";
import Select from "Components/Admin/Form/Select";
import Section from "Components/Admin/Section";
import Segmentation from "Components/Admin/Segmentation";
import { TextEditor } from "Components/Admin/TextEditor";
import ToggleStatus from "Components/Admin/ToggleStatus";

import * as S from "./styles";

const level_options = [
  { value: "regular", label: "Regular" },
  { value: "toast", label: "Toast" },
  { value: "email", label: "E-mail" },
  { value: "popup", label: "Pop-up" },
];

const UpdateNotification = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { fetchData, loading } = useFetch();
  const {
    setSegmentationConfig,
    stringifySegmentationState,
    setDefaultSegmentation,
  } = useSegmentation({ configs: { pageType: "update" } });

  const useStates = { useAll: true };
  const segmentationConfig = setSegmentationConfig(useStates);

  const [selectedNotification, setSelectedNotification] = useState({});
  const [editorState, setEditorState] = useState(EditorState.createEmpty());

  const [selectedLevels, setSelectedLevels] = useState([]);
  const [selectedRoute, setSelectedRoute] = useState(null);

  const [notificationTypes, setNotificationTypes] = useState([]);
  const [selectedType, setSelectedType] = useState({});

  useEffect(() => {
    if (id) {
      getNotification(id);
    }
    // eslint-disable-next-line
  }, [id]);

  const methods = useFormSchema(NotificationValidator, {
    defaultValues: {
      start_at: null,
      finish_at: null,
    },
  });

  const {
    register,
    formState: { isDirty },
    setValue,
  } = methods;

  const getNotificationTypes = async () => {
    const response = await fetchData({
      method: "get",
      path: "/notifications/get-notification-types",
    });

    if (!response?.data) return;
    const notificationTypes = response.data;

    const options = notificationTypes.map((notificationType) => ({
      // icon: notificationType.icon,
      value: notificationType.id,
      label: notificationType.display_name,
    }));

    setNotificationTypes(options);

    return options;
  };

  const getNotification = async (id) => {
    const onSuccessCallback = async (data) => {
      const notification = data;
      setSelectedNotification(notification);

      setAllFormValues(notification, setValue, true);

      setEditorState(
        addHTMLToEditor(notification.description, EditorState.createEmpty()),
      );

      if (notification.levels?.length)
        setSelectedLevels(
          notification.levels?.map((level) =>
            level_options.find((option) => option.label === level),
          ),
        );

      const notificationTypeOptions = await getNotificationTypes();

      setSelectedType(
        notificationTypeOptions.find(
          (option) => option.value === notification.notification_type_id,
        ),
      );

      if (notification.route) {
        const route = Object.keys(AuthRoutes).find(
          (route) => AuthRoutes[route].path === notification.route,
        );

        setSelectedRoute({
          value: AuthRoutes[route].path,
          label: AuthRoutes[route].displayName,
        });
      }

      setDefaultSegmentation(data, useStates);
    };

    await fetchData({
      method: "post",
      path: "/notifications/get-notification-by-id",
      body: { notification_id: id },
      onSuccessCallback,
    });
  };

  const submit = async (data) => {
    const successMessage = "Notificação atualizada com sucesso!";

    data.description = transformEditorStateIntoData(editorState);

    await fetchData({
      method: "post",
      path: "/notifications/update-notification",
      body: {
        notification_id: selectedNotification.id,
        notification_type_id: selectedType?.value,
        levels: JSON.stringify(selectedLevels.map((level) => level.value)),
        route: selectedRoute?.value,
        ...data,
        ...stringifySegmentationState(),
      },
      successMessage,
      config: {
        show_success_message: true,
      },
    });
  };

  const is_not_editable = selectedNotification.levels?.includes("E-mail");

  return (
    <Section>
      <S.Wrapper>
        <S.Buttons>
          <Button
            type="button"
            text="Voltar"
            disabled={loading}
            onClick={() => navigate(AdminRoutes.ListNotifications.path)}
            width="30%"
          />

          <ToggleStatus
            route={"/notifications/toggle-notification-status/"}
            item={selectedNotification}
            refresh={() => getNotification(id)}
            disabled={is_not_editable}
          />
        </S.Buttons>

        <FormWrapper {...methods} submit={submit}>
          {is_not_editable && (
            <span>
              Esta notificação não pode ser editada, pois já foi enviada para o
              usuário via e-mail.
            </span>
          )}

          <Input
            {...register("title")}
            type="text"
            label="Título"
            placeholder="Título"
            required
            disabled={is_not_editable}
          />

          <TextEditor
            {...register("description")}
            editorState={editorState}
            setEditorState={setEditorState}
            key="description"
            label="Descrição"
            required
            disabled={is_not_editable}
          />

          <DateValidity register={register} disabled={is_not_editable} />

          <Input
            {...register("link")}
            type="text"
            label="Link"
            placeholder="Link"
            disabled={is_not_editable}
          />

          <S.Type>
            <Select
              name="type"
              label="Categoria"
              placeholder={"Selecione uma Categoria"}
              options={notificationTypes}
              value={selectedType}
              onChange={setSelectedType}
              disabled={is_not_editable}
            />

            <Button
              type="button"
              text="Criar Categoria"
              onClick={() => navigate(AdminRoutes.CreateNotificationType.path)}
              disabled={loading || !isDirty || is_not_editable}
            />
          </S.Type>

          <Select
            name="levels"
            label="Níveis"
            placeholder={"Selecione os Níveis"}
            options={level_options}
            value={selectedLevels}
            onChange={setSelectedLevels}
            required
            disabled={is_not_editable}
            isMulti
          />

          <Segmentation configs={segmentationConfig} />

          <Button
            type="submit"
            text="Atualizar"
            disabled={loading || !isDirty || is_not_editable}
          />
        </FormWrapper>
      </S.Wrapper>
    </Section>
  );
};

export default UpdateNotification;
